import { useEffect, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { OtpInput } from '../components/OtpInput';
import { SuccessAnimation } from '../components/SuccessAnimation';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Bildmarke, Schriftzug } from '../components/Marke';
import { transition, uebergangSanft } from '../lib/motion';
import {
  DEMO,
  ApiFehler,
  type Anmeldearten,
  anmeldearten as apiAnmeldearten,
  anmeldenMitPasswort,
  codeErneutSenden,
  emailBestaetigen,
  emailPruefen,
  googleStartUrl,
  passwortVergessen,
  passwortZuruecksetzen,
  registrieren,
} from '../lib/api';

/**
 * Anmeldung und Registrierung in einem Formular mit mehreren Schritten
 * (Bauplan „Registrierung & Login“, Abschnitt 5):
 *   E-Mail → Passwort | Registrieren | Passwort festlegen → Code → angemeldet
 *   Passwort vergessen → Code + neues Passwort → angemeldet
 */

/** Testcode nur in der Vorschau (Demo-Build). */
const DEMO_CODE = '123456';
const PASSWORT_MIN = 8;
const EMAIL_MUSTER = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const NEU_SENDEN_SEKUNDEN = 60;

type Reiter = 'anmelden' | 'registrieren';
type Schritt = 'email' | 'passwort' | 'registrieren' | 'festlegen' | 'code' | 'vergessen' | 'zuruecksetzen' | 'fertig';

/** Klartext zu den Fehlerkennungen aus der Rückkehr von Google. */
const ANMELDEFEHLER: Record<string, string> = {
  abgebrochen: 'Die Anmeldung mit Google wurde abgebrochen.',
  abgelaufen: 'Der Anmeldevorgang ist abgelaufen. Bitte erneut anmelden.',
  fremder_browser: 'Die Anmeldung muss im selben Browser abgeschlossen werden, in dem sie begonnen wurde.',
  email_unbestaetigt: 'Das Google-Konto hat keine bestätigte E-Mail-Adresse.',
  anbieter_nicht_erreichbar: 'Google ist derzeit nicht erreichbar. Bitte später erneut versuchen.',
  zugang_gesperrt: 'Der Zugang ist gesperrt. Bitte wenden Sie sich an den Betreiber.',
  ungueltig: 'Die Anmeldung konnte nicht bestätigt werden. Bitte erneut versuchen.',
};

const AGB_URL = import.meta.env.VITE_AGB_URL as string | undefined;
const DATENSCHUTZ_URL = import.meta.env.VITE_DATENSCHUTZ_URL as string | undefined;

export interface LoginProps {
  onAnmeldung: () => void;
  /** Fehlerkennung nach Rückkehr von Google. */
  anmeldefehler?: string | null;
  /** Hinweis, wenn die vorige Sitzung abgelaufen ist. */
  sitzungAbgelaufen?: boolean;
}

const fehlerText = (f: unknown, ersatz: string) => (f instanceof ApiFehler && f.status !== 0 ? f.message : f instanceof ApiFehler ? f.message : ersatz);

export function Login({ onAnmeldung, anmeldefehler, sitzungAbgelaufen }: LoginProps) {
  const reduziert = useReducedMotion();
  const startReiter: Reiter =
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('mode') === 'register'
      ? 'registrieren'
      : 'anmelden';

  const [reiter, setReiter] = useState<Reiter>(startReiter);
  const [schritt, setSchritt] = useState<Schritt>(startReiter === 'registrieren' ? 'registrieren' : 'email');
  const [arten, setArten] = useState<Anmeldearten | null>(null);

  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  const [passwort2, setPasswort2] = useState('');
  const [vorname, setVorname] = useState('');
  const [nachname, setNachname] = useState('');
  const [firma, setFirma] = useState('');
  const [code, setCode] = useState('');

  const [fehler, setFehler] = useState<string | null>(anmeldefehler ? ANMELDEFEHLER[anmeldefehler] ?? ANMELDEFEHLER.ungueltig : null);
  const [hinweis, setHinweis] = useState<string | null>(sitzungAbgelaufen ? 'Die Sitzung ist abgelaufen. Bitte erneut anmelden.' : null);
  const [laeuft, setLaeuft] = useState(false);
  const [testCode, setTestCode] = useState<string | null>(null);
  const [sekundenBisNeu, setSekundenBisNeu] = useState(0);
  const pruefSperre = useRef(false);

  useEffect(() => {
    let aktiv = true;
    apiAnmeldearten()
      .then((a) => aktiv && setArten(a))
      .catch(() => aktiv && setArten({ google: false, registrierung: true }));
    return () => {
      aktiv = false;
    };
  }, []);

  useEffect(() => {
    if (sekundenBisNeu <= 0) return;
    const id = window.setInterval(() => setSekundenBisNeu((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(id);
  }, [sekundenBisNeu]);

  function wechsle(neu: Schritt) {
    setFehler(null);
    setSchritt(neu);
  }

  function reiterWechseln(r: Reiter) {
    setReiter(r);
    setFehler(null);
    setHinweis(null);
    setPasswort('');
    setPasswort2('');
    setSchritt(r === 'registrieren' ? 'registrieren' : 'email');
  }

  /** Gemeinsamer Ablauf für alle Formularschritte: Sperre, Fehlertext, Timeout. */
  async function ausfuehren(aktion: () => Promise<void>, ersatz: string) {
    if (laeuft) return;
    setLaeuft(true);
    setFehler(null);
    try {
      await Promise.race([
        aktion(),
        new Promise<never>((_, nein) =>
          window.setTimeout(() => nein(new ApiFehler(0, 'Der Server antwortet nicht. Bitte erneut versuchen.')), 20000),
        ),
      ]);
    } catch (f) {
      setFehler(fehlerText(f, ersatz));
    } finally {
      setLaeuft(false);
    }
  }

  function codeErwartet(neuerTestCode?: string) {
    setTestCode(DEMO ? DEMO_CODE : neuerTestCode ?? null);
    setCode('');
    setSekundenBisNeu(NEU_SENDEN_SEKUNDEN);
    wechsle('code');
  }

  /* ---------------------------------------------------------------- Schritte */

  function weiterMitEmail(e: FormEvent) {
    e.preventDefault();
    if (!EMAIL_MUSTER.test(email.trim())) {
      setFehler('Bitte eine gültige E-Mail-Adresse angeben.');
      return;
    }
    void ausfuehren(async () => {
      const status = DEMO ? 'login' : await emailPruefen(email.trim());
      if (status === 'login') wechsle('passwort');
      else if (status === 'set_password') wechsle('festlegen');
      else {
        setReiter('registrieren');
        setHinweis('Für diese E-Mail-Adresse besteht noch kein Konto. Bitte registrieren Sie sich.');
        wechsle('registrieren');
      }
    }, 'Die E-Mail-Adresse konnte nicht geprüft werden.');
  }

  function anmelden(e: FormEvent) {
    e.preventDefault();
    void ausfuehren(async () => {
      if (DEMO) return wechsle('fertig');
      try {
        await anmeldenMitPasswort(email.trim(), passwort);
        wechsle('fertig');
      } catch (f) {
        const details = f instanceof ApiFehler ? (f.details as { needsVerification?: boolean; testCode?: string } | undefined) : undefined;
        if (details?.needsVerification) {
          setHinweis('Bitte bestätigen Sie Ihre E-Mail-Adresse. Wir haben Ihnen einen Code gesendet.');
          codeErwartet(details.testCode);
          return;
        }
        throw f;
      }
    }, 'Die Anmeldung ist fehlgeschlagen.');
  }

  function passwortRegelnOk(): boolean {
    if (passwort.length < PASSWORT_MIN) {
      setFehler(`Das Passwort muss mindestens ${PASSWORT_MIN} Zeichen lang sein.`);
      return false;
    }
    if (passwort !== passwort2) {
      setFehler('Die Passwörter stimmen nicht überein.');
      return false;
    }
    return true;
  }

  function registrierenAbsenden(e: FormEvent) {
    e.preventDefault();
    if (!EMAIL_MUSTER.test(email.trim())) return setFehler('Bitte eine gültige E-Mail-Adresse angeben.');
    if (!vorname.trim()) return setFehler('Bitte den Vornamen angeben.');
    if (!passwortRegelnOk()) return;
    void ausfuehren(async () => {
      if (DEMO) return codeErwartet();
      const r = await registrieren({
        email: email.trim(),
        password: passwort,
        firstName: vorname.trim(),
        lastName: nachname.trim() || undefined,
        firma: firma.trim() || undefined,
      });
      setHinweis(null);
      codeErwartet(r.testCode);
    }, 'Die Registrierung ist fehlgeschlagen.');
  }

  function codePruefen(eingabe: string) {
    if (pruefSperre.current) return;
    pruefSperre.current = true;
    void ausfuehren(async () => {
      if (DEMO) {
        if (eingabe !== DEMO_CODE) throw new ApiFehler(400, 'Der Code ist ungültig oder abgelaufen.');
        return wechsle('fertig');
      }
      await emailBestaetigen(email.trim(), eingabe);
      wechsle('fertig');
    }, 'Der Code ist ungültig oder abgelaufen.').finally(() => {
      pruefSperre.current = false;
      setCode('');
    });
  }

  function codeNeuSenden() {
    setSekundenBisNeu(NEU_SENDEN_SEKUNDEN);
    if (DEMO) return;
    void codeErneutSenden(email.trim())
      .then((r) => r.testCode && setTestCode(r.testCode))
      .catch((f) => setFehler(fehlerText(f, 'Der Code konnte nicht gesendet werden.')));
  }

  function vergessenAbsenden(e: FormEvent) {
    e.preventDefault();
    if (!EMAIL_MUSTER.test(email.trim())) return setFehler('Bitte eine gültige E-Mail-Adresse angeben.');
    void ausfuehren(async () => {
      const r = DEMO ? { testCode: DEMO_CODE } : await passwortVergessen(email.trim());
      setTestCode(r.testCode ?? null);
      setCode('');
      setPasswort('');
      setPasswort2('');
      setHinweis('Falls ein Konto zu dieser Adresse besteht, wurde ein Code gesendet.');
      wechsle('zuruecksetzen');
    }, 'Die Anfrage ist fehlgeschlagen.');
  }

  function zuruecksetzenAbsenden(e: FormEvent) {
    e.preventDefault();
    if (code.length !== 6) return setFehler('Bitte den sechsstelligen Code eingeben.');
    if (!passwortRegelnOk()) return;
    void ausfuehren(async () => {
      if (!DEMO) await passwortZuruecksetzen(email.trim(), code, passwort);
      wechsle('fertig');
    }, 'Das Passwort konnte nicht zurückgesetzt werden.');
  }

  /* ---------------------------------------------------------------- Darstellung */

  const google = !!arten?.google && (schritt === 'email' || schritt === 'registrieren');
  const zeigeReiter = schritt === 'email' || schritt === 'registrieren';

  return (
    <main className="flex min-h-dvh items-center justify-center bg-bg px-gutter py-6">
      <div className="w-full max-w-md">
        <div className="mb-5 flex items-center gap-3">
          <Bildmarke groesse={56} puls="deutlich" className="text-fg" titel="GrundPuls" />
          <div>
            <p className="text-2xl leading-tight"><Schriftzug /></p>
            <p className="text-sm text-fg-muted">Betriebskosten- und Immobilienabrechnung</p>
          </div>
        </div>

        <motion.div
          layout
          transition={transition(!!reduziert, { type: 'spring', stiffness: 260, damping: 32 })}
          className="rounded-xl border border-border bg-surface p-5 shadow-md"
        >
          {zeigeReiter && (
            <div role="tablist" aria-label="Anmeldung oder Registrierung" className="mb-5 grid grid-cols-2 rounded-lg bg-surface-2 p-1">
              {(['anmelden', 'registrieren'] as const).map((r) => (
                <button
                  key={r}
                  role="tab"
                  type="button"
                  aria-selected={reiter === r}
                  onClick={() => reiterWechseln(r)}
                  className="relative h-10 rounded-md text-sm font-medium text-fg-muted transition aria-selected:text-fg"
                >
                  {reiter === r && (
                    <motion.span
                      layoutId="login-reiter"
                      transition={transition(!!reduziert, { type: 'spring', stiffness: 420, damping: 36 })}
                      className="absolute inset-0 rounded-md bg-surface shadow-sm"
                    />
                  )}
                  <span className="relative">{r === 'anmelden' ? 'Anmelden' : 'Registrieren'}</span>
                </button>
              ))}
            </div>
          )}

          {(fehler || hinweis) && (
            <div className="mb-4 space-y-2">
              {fehler && (
                <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger-soft-fg">
                  {fehler}
                </p>
              )}
              {hinweis && !fehler && (
                <p className="rounded-lg bg-accent-soft px-3 py-2 text-sm text-accent-soft-fg">{hinweis}</p>
              )}
            </div>
          )}

          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={schritt}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={transition(!!reduziert, uebergangSanft)}
            >
              {schritt === 'email' && (
                <form className="space-y-4" onSubmit={weiterMitEmail} noValidate>
                  <Titel text="Anmeldung" unter="Mit Ihrer E-Mail-Adresse und Ihrem Passwort." />
                  <Input
                    label="E-Mail-Adresse"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                  />
                  <Button type="submit" volleBreite groesse="lg" ladend={laeuft}>
                    Weiter
                  </Button>
                </form>
              )}

              {schritt === 'passwort' && (
                <form className="space-y-4" onSubmit={anmelden}>
                  <Titel text="Passwort eingeben" />
                  <EmailZeile email={email} onAendern={() => wechsle('email')} />
                  <Input
                    label="Passwort"
                    type="password"
                    autoComplete="current-password"
                    value={passwort}
                    onChange={(e) => setPasswort(e.target.value)}
                    autoFocus
                  />
                  <Button type="submit" volleBreite groesse="lg" ladend={laeuft}>
                    Anmelden
                  </Button>
                  <TextKnopf onClick={() => wechsle('vergessen')}>Passwort vergessen?</TextKnopf>
                </form>
              )}

              {(schritt === 'registrieren' || schritt === 'festlegen') && (
                <form className="space-y-4" onSubmit={registrierenAbsenden} noValidate>
                  {schritt === 'festlegen' ? (
                    <>
                      <Titel
                        text="Passwort festlegen"
                        unter="Für dieses Konto ist noch kein Passwort festgelegt. Zur Bestätigung erhalten Sie einen Code per E-Mail."
                      />
                      <EmailZeile email={email} onAendern={() => wechsle('email')} />
                    </>
                  ) : (
                    <>
                      <Titel text="Konto anlegen" unter="Die Testphase beginnt nach der Bestätigung Ihrer E-Mail-Adresse." />
                      <Input
                        label="E-Mail-Adresse"
                        type="email"
                        autoComplete="email"
                        inputMode="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </>
                  )}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Input label="Vorname" autoComplete="given-name" value={vorname} onChange={(e) => setVorname(e.target.value)} />
                    <Input label="Nachname" autoComplete="family-name" value={nachname} onChange={(e) => setNachname(e.target.value)} />
                  </div>
                  {schritt === 'registrieren' && (
                    <Input
                      label="Firma (optional)"
                      autoComplete="organization"
                      value={firma}
                      onChange={(e) => setFirma(e.target.value)}
                    />
                  )}
                  <Input
                    label="Passwort"
                    type="password"
                    autoComplete="new-password"
                    hinweis={`Mindestens ${PASSWORT_MIN} Zeichen.`}
                    value={passwort}
                    onChange={(e) => setPasswort(e.target.value)}
                  />
                  <Input
                    label="Passwort wiederholen"
                    type="password"
                    autoComplete="new-password"
                    value={passwort2}
                    onChange={(e) => setPasswort2(e.target.value)}
                  />
                  <Button type="submit" volleBreite groesse="lg" ladend={laeuft}>
                    {schritt === 'festlegen' ? 'Passwort festlegen' : 'Registrieren'}
                  </Button>
                  <Rechtliches />
                </form>
              )}

              {schritt === 'code' && (
                <div className="space-y-4">
                  <Titel text="E-Mail-Adresse bestätigen" />
                  <p className="text-sm leading-relaxed text-fg-muted">
                    Ein sechsstelliger Code wurde an <span className="font-medium text-fg">{email}</span> gesendet.
                    Er ist 15 Minuten gültig.
                  </p>
                  <OtpInput
                    wert={code}
                    onWert={(w) => {
                      setCode(w);
                      if (fehler) setFehler(null);
                    }}
                    onVollstaendig={codePruefen}
                    fehler={!!fehler}
                    gesperrt={laeuft}
                    autoFokus
                    ariaLabel="Bestätigungscode"
                  />
                  {laeuft && <p className="text-sm text-fg-muted">Code wird geprüft …</p>}
                  <Button variante="sekundaer" volleBreite disabled={sekundenBisNeu > 0} onClick={codeNeuSenden}>
                    {sekundenBisNeu > 0 ? `Code erneut senden in ${sekundenBisNeu} s` : 'Code erneut senden'}
                  </Button>
                  <TextKnopf onClick={() => reiterWechseln('anmelden')}>Andere E-Mail-Adresse verwenden</TextKnopf>
                  <TestCodeHinweis code={testCode} />
                </div>
              )}

              {schritt === 'vergessen' && (
                <form className="space-y-4" onSubmit={vergessenAbsenden} noValidate>
                  <Titel text="Passwort zurücksetzen" unter="Sie erhalten einen Code an Ihre E-Mail-Adresse." />
                  <Input
                    label="E-Mail-Adresse"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <Button type="submit" volleBreite groesse="lg" ladend={laeuft}>
                    Code anfordern
                  </Button>
                  <TextKnopf onClick={() => reiterWechseln('anmelden')}>Zurück zur Anmeldung</TextKnopf>
                </form>
              )}

              {schritt === 'zuruecksetzen' && (
                <form className="space-y-4" onSubmit={zuruecksetzenAbsenden} noValidate>
                  <Titel text="Neues Passwort festlegen" />
                  <EmailZeile email={email} onAendern={() => wechsle('vergessen')} />
                  <Input
                    label="Code aus der E-Mail"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  />
                  <Input
                    label="Neues Passwort"
                    type="password"
                    autoComplete="new-password"
                    hinweis={`Mindestens ${PASSWORT_MIN} Zeichen.`}
                    value={passwort}
                    onChange={(e) => setPasswort(e.target.value)}
                  />
                  <Input
                    label="Neues Passwort wiederholen"
                    type="password"
                    autoComplete="new-password"
                    value={passwort2}
                    onChange={(e) => setPasswort2(e.target.value)}
                  />
                  <Button type="submit" volleBreite groesse="lg" ladend={laeuft}>
                    Passwort speichern und anmelden
                  </Button>
                  <TestCodeHinweis code={testCode} />
                </form>
              )}

              {schritt === 'fertig' && (
                <div className="py-4">
                  <SuccessAnimation
                    titel="Anmeldung bestätigt"
                    beschreibung="Die Sitzung wird geöffnet."
                    ton="akzent"
                    onFertig={onAnmeldung}
                  />
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {google && (
            <>
              <div className="my-5 flex items-center gap-3 text-xs text-fg-subtle">
                <span className="h-px flex-1 bg-border" />
                oder
                <span className="h-px flex-1 bg-border" />
              </div>
              <a
                href={googleStartUrl()}
                onClick={(e) => {
                  if (!DEMO) return;
                  e.preventDefault();
                  wechsle('fertig');
                }}
                className="flex h-12 w-full items-center justify-center rounded-lg border border-border-strong bg-surface px-5 text-base font-medium text-fg transition hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface active:scale-[0.99]"
              >
                Mit Google anmelden
              </a>
            </>
          )}
        </motion.div>

        <p className="mt-4 text-center text-xs leading-relaxed text-fg-subtle">
          Mieterdaten werden ausschließlich zur Erstellung der Abrechnung verarbeitet.
        </p>
      </div>
    </main>
  );
}

function Titel({ text, unter }: { text: string; unter?: string }) {
  return (
    <div>
      <h1 className="text-lg font-semibold text-fg">{text}</h1>
      {unter && <p className="mt-1 text-sm leading-relaxed text-fg-muted">{unter}</p>}
    </div>
  );
}

function EmailZeile({ email, onAendern }: { email: string; onAendern: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg bg-surface-2 px-3 py-2">
      <span className="min-w-0 truncate text-sm text-fg">{email}</span>
      <button type="button" onClick={onAendern} className="shrink-0 text-sm font-medium text-accent hover:underline">
        Ändern
      </button>
    </div>
  );
}

function TextKnopf({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="block w-full py-1 text-center text-sm font-medium text-accent hover:underline">
      {children}
    </button>
  );
}

function TestCodeHinweis({ code }: { code: string | null }) {
  if (!code) return null;
  return (
    <p className="rounded-lg bg-accent-soft px-3 py-2 text-center text-sm text-accent-soft-fg">
      {DEMO ? 'Testzugang dieser Vorschau' : 'Testbetrieb ohne E-Mail-Versand'}: Code{' '}
      <span className="font-semibold">{code}</span>
    </p>
  );
}

function Rechtliches() {
  const link = (url: string | undefined, text: string) =>
    url ? (
      <a href={url} target="_blank" rel="noreferrer" className="underline hover:text-fg">
        {text}
      </a>
    ) : (
      text
    );
  return (
    <p className="text-center text-xs leading-relaxed text-fg-subtle">
      Mit der Registrierung akzeptieren Sie die {link(AGB_URL, 'AGB')} und die{' '}
      {link(DATENSCHUTZ_URL, 'Datenschutzerklärung')}.
    </p>
  );
}

export default Login;
